/**
 * Load all company data stores from the API.
 * Call this after successful login, registration, or invite acceptance.
 */
import { assetStore, assetActions } from '../stores/assetStore';
import { riskStore, riskActions } from '../stores/riskStore';
import { nonInfoSecRiskStore, nonInfoSecRiskActions } from '../stores/nonInfoSecRiskStore';
import { loadAdminFromAPI } from '../stores/adminStore';
import { globalConfigActions } from '../stores/globalConfigStore';
import { riskMatrixActions } from '../stores/riskMatrixStore';
import { soaStore, soaActions } from '../stores/soaStore';
import { raciStore, raciActions } from '../stores/raciStore';

export async function loadCompanyData(): Promise<void> {
  await Promise.allSettled([
    globalConfigActions.load(),
    assetActions.loadAssets(),
    riskActions.loadRisks(),
    nonInfoSecRiskActions.loadAll(),
    loadAdminFromAPI(),
    riskMatrixActions.loadConfig(),
    soaActions.loadAll(),
    raciActions.loadAll(),
  ]);
}

/**
 * Clear all company-specific data from stores.
 * Call this before switching to a different company to prevent data bleed.
 */
export function clearCompanyData(): void {
  // Asset store
  assetStore.assets = [];
  assetStore.loading = true;
  assetStore.searchTerm = '';
  assetStore.iso27001Filter = 'all';

  // Risk store
  riskStore.risks = [];
  riskStore.loading = true;
  riskStore.searchTerm = '';
  riskStore.categoryFilter = 'all';
  riskStore.statusFilter = 'all';
  riskStore.treatmentFilter = 'all';

  // Non-InfoSec risk store
  nonInfoSecRiskStore.risks = [];
  nonInfoSecRiskStore.loading = true;
  nonInfoSecRiskStore.searchTerm = '';
  nonInfoSecRiskStore.categoryFilter = 'all';
  nonInfoSecRiskStore.statusFilter = 'all';
  nonInfoSecRiskStore.treatmentFilter = 'all';

  // SoA store
  soaStore.justifications = {};
  soaStore.companyInfo = {
    issueNo: '1.00',
    date: new Date().toISOString().split('T')[0],
    companyName: 'Your Company Name',
    logo: '',
    preparedBy: '',
    reviewedBy: '',
    approvedBy: '',
    approvedDate: '',
    nextReviewDate: '',
    versionHistory: [],
  };
  soaStore.loading = true;
  soaStore.showUsed = true;
  soaStore.showUnused = true;
  soaStore.filterSection = 'all';
  soaStore.searchTerm = '';

  // RACI store
  raciStore.mode = 'soa';
  raciStore.assignments = {};
  raciStore.users = [];
  raciStore.loading = true;
  raciStore.searchTerm = '';
  raciStore.filterGroup = 'all';
  raciStore.filterAssigned = 'all';
  raciStore.filterUsage = 'all';
}

/**
 * Switch company data: clear all stores and reload from the API.
 * Call this after a successful company switch.
 */
export async function switchCompanyData(): Promise<void> {
  clearCompanyData();
  await loadCompanyData();
}
